<template>
  <div class="container mt-5 py-5">
    <div class="row justify-content-center mt-5 align-items-center min-vh-100">
      <div class="col-md-6 col-lg-5">
        <div class="login-box bg-dark p-4 shadow rounded-3">
          <!-- Logo/Título -->
          <div class="text-center mb-4">
            <img src="@/assets/images/logo.png" alt="semillero del oeste logo" class="img-fluid col-3">
            <h4 class="text-light mt-3">{{ showVerification ? 'Verificar Email' : 'Crear Cuenta' }}</h4>
          </div>

          <!-- Formulario de Verificación -->
          <div v-if="showVerification" class="verification-form">
            <p class="text-center text-white mb-4">
              Hemos enviado un código de verificación a tu email:<br>
              <strong>{{ formData.email }}</strong>
            </p>

            <div class="verification-code-input mb-4">
              <div class="form-floating">
                <input
                  type="text"
                  class="form-control text-center"
                  v-model="verificationCode"
                  maxlength="6"
                  placeholder="Código de verificación"
                  @input="handleCodeInput"
                >
                <label>Ingresa el código de 6 dígitos</label>
              </div>
            </div>

            <div class="text-center mb-3">
              <button 
                @click="verifyCode" 
                class="btn btn-primary w-100 py-3"
                :disabled="isVerifying || verificationCode.length !== 6"
              >
                <span v-if="isVerifying" class="spinner-border spinner-border-sm me-2"></span>
                {{ isVerifying ? 'Verificando...' : 'Verificar' }}
              </button>
            </div>

            <div class="text-center">
              <p class="text-muted mb-2">¿No recibiste el código?</p>
              <button 
                @click="resendCode" 
                class="btn btn-link"
                :disabled="isResending"
              >
                {{ isResending ? 'Enviando...' : 'Reenviar código' }}
              </button>
            </div>
          </div>

          <!-- Formulario de Registro -->
          <form v-else @submit.prevent="handleSubmit" class="needs-validation" novalidate>
            <!-- Nombre y Apellido -->
            <div class="row mb-3">
              <div class="col-12 mb-3">
                <div class="form-floating">
                  <input
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': v$.first_name.$error }"
                    id="first_name"
                    v-model="formData.first_name"
                    placeholder="Nombre"
                    required
                  >
                  <label for="first_name">
                    <i class="fas fa-user text-muted me-2"></i>Nombre
                  </label>
                  <div class="invalid-feedback" v-if="v$.first_name.$error">
                    {{ v$.first_name.$errors[0].$message }}
                  </div>
                </div>
              </div>
              <div class="col-12">
                <div class="form-floating">
                  <input
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': v$.last_name.$error }"
                    id="last_name"
                    v-model="formData.last_name"
                    placeholder="Apellido"
                    required
                  >
                  <label for="last_name">
                    <i class="fas fa-user text-muted me-2"></i>Apellido
                  </label>
                  <div class="invalid-feedback" v-if="v$.last_name.$error">
                    {{ v$.last_name.$errors[0].$message }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Campo Usuario -->
            <div class="form-floating mb-3">
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': v$.username.$error }"
                id="username"
                v-model="formData.username"
                placeholder="Usuario"
                required
              >
              <label for="username">
                <i class="fas fa-user text-muted me-2"></i>Usuario
              </label>
              <div class="invalid-feedback" v-if="v$.username.$error">
                {{ v$.username.$errors[0].$message }}
              </div>
            </div>

            <!-- Campo Instagram (opcional) -->
            <div class="form-floating mb-3">
              <input
                type="text"
                class="form-control"
                id="instagram"
                v-model="formData.instagram"
                placeholder="Instagram (opcional)"
              >
              <label for="instagram">
                <i class="fab fa-instagram text-muted me-2"></i>Instagram (opcional)
              </label>
              
              <!-- Tooltip de información -->
              <div class="instagram-tooltip">
                <i class="fas fa-info-circle text-primary"></i>
                <div class="tooltip-content">
                  Es importante que proporciones tu Instagram ya que lo utilizaremos como medio de comunicación alternativo si no podemos contactarte por email.
                </div>
              </div>
            </div>

            <!-- Campo Email -->
            <div class="form-floating mb-3">
              <input
                type="email"
                class="form-control"
                :class="{ 'is-invalid': v$.email.$error }"
                id="email"
                v-model="formData.email"
                placeholder="Email"
                required
              >
              <label for="email">
                <i class="fas fa-envelope text-muted me-2"></i>Email
              </label>
              <div class="invalid-feedback" v-if="v$.email.$error">
                {{ v$.email.$errors[0].$message }}
              </div>
            </div>

            <!-- Campo Contraseña -->
            <div class="form-floating mb-3">
              <input
                :type="showPassword ? 'text' : 'password'"
                class="form-control"
                :class="{ 'is-invalid': v$.password.$error }"
                id="password"
                v-model="formData.password"
                placeholder="Contraseña"
                required
              >
              <label for="password">
                <i class="fas fa-lock text-muted me-2"></i>Contraseña
              </label>
              <span 
                class="password-toggle"
                @click="showPassword = !showPassword"
              >
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </span>
              <div class="invalid-feedback" v-if="v$.password.$error">
                {{ v$.password.$errors[0].$message }}
              </div>
            </div>

            <!-- Campo Confirmar Contraseña -->
            <div class="form-floating mb-3">
              <input
                :type="showConfirmPassword ? 'text' : 'password'"
                class="form-control"
                :class="{ 'is-invalid': v$.password2.$error }"
                id="confirmPassword"
                v-model="formData.password2"
                placeholder="Confirmar Contraseña"
                required
              >
              <label for="confirmPassword">
                <i class="fas fa-lock text-muted me-2"></i>Confirmar Contraseña
              </label>
              <span 
                class="password-toggle"
                @click="showConfirmPassword = !showConfirmPassword"
              >
                <i :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </span>
              <div class="invalid-feedback" v-if="v$.password2.$error">
                {{ v$.password2.$errors[0].$message }}
              </div>
            </div>

            <!-- Mensaje de error -->
            <div v-if="errorMessage" class="alert alert-danger mb-3">
              {{ errorMessage }}
            </div>

            <!-- Mensaje de cuenta existente no verificada -->
            <div v-if="showAccountExistsMessage" class="alert alert-success mb-3">
              <i class="fas fa-info-circle me-2"></i>
              {{ accountExistsMessage }}
              <br>
              <router-link to="/login" class="text-success fw-bold text-decoration-none">
                <i class="fas fa-sign-in-alt me-1"></i>
                Ir a Iniciar Sesión
              </router-link>
            </div>

            <!-- Botón Submit -->
            <button
              type="submit"
              class="btn btn-primary w-100 mb-3 py-3"
              :disabled="isLoading"
            >
              <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
              {{ isLoading ? 'Creando cuenta...' : 'Crear Cuenta' }}
            </button>

            <!-- Link a Login -->
            <div class="text-center">
              <p class="text-muted">
                ¿Ya tienes una cuenta? 
                <router-link to="/login" class="text-primary text-decoration-none">
                  Iniciar Sesión
                </router-link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, minLength, email, helpers } from '@vuelidate/validators'
import { useRouter } from 'vue-router'
import { register, verifyEmail, resendVerification } from '@/services/auth'
import { useToast } from 'vue-toastification'

const router = useRouter()
const toast = useToast()

// Estado del formulario - Corregido para usar password2
const formData = reactive({
  first_name: '',
  last_name: '',
  username: '',
  instagram: '',
  email: '',
  password: '',
  password2: ''  // Cambiado de confirmPassword a password2
})

// Estado UI
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const isLoading = ref(false)
const errorMessage = ref('')
const showAccountExistsMessage = ref(false)
const accountExistsMessage = ref('')

const showVerification = ref(false)
const verificationCode = ref('')
const isVerifying = ref(false)
const isResending = ref(false)
const registeredEmail = ref('')

// Validador personalizado para confirmar contraseña
const sameAsPassword = computed(() => {
  return helpers.withMessage(
    'Las contraseñas no coinciden',
    (value) => value === formData.password
  )
})
onMounted(() => {
  // seteo el titulo de la página
  document.title = 'Crear Cuenta - Semillero del Oeste'
});

// Reglas de validación actualizadas
const rules = {
  first_name: { 
    required: helpers.withMessage('El nombre es requerido', required),
    minLength: helpers.withMessage('El nombre debe tener al menos 2 caracteres', minLength(2))
  },
  last_name: { 
    required: helpers.withMessage('El apellido es requerido', required),
    minLength: helpers.withMessage('El apellido debe tener al menos 2 caracteres', minLength(2))
  },
  username: { 
    required: helpers.withMessage('El nombre de usuario es requerido', required),
    minLength: helpers.withMessage(
      'El nombre de usuario debe tener al menos 4 caracteres',
      minLength(4)
    )
  },
  email: { 
    required: helpers.withMessage('El email es requerido', required),
    email: helpers.withMessage('Ingrese un email válido', email)
  },
  password: { 
    required: helpers.withMessage('La contraseña es requerida', required),
    minLength: helpers.withMessage(
      'La contraseña debe tener al menos 6 caracteres',
      minLength(6)
    )
  },
  password2: {  // Cambiado de confirmPassword a password2
    required: helpers.withMessage('Debe confirmar la contraseña', required),
    sameAsPassword
  }
}

const v$ = useVuelidate(rules, formData)

// Manejo del submit actualizado
const handleSubmit = async () => {
  try {
    errorMessage.value = ''
    showAccountExistsMessage.value = false
    const isFormValid = await v$.value.$validate()
    
    if (!isFormValid) {
      const firstError = Object.values(v$.value.$errors)[0]
      if (firstError) {
        toast.error(firstError.$message)
      }
      return
    }
    
    isLoading.value = true
    
    const response = await register(formData)
    showVerification.value = true
    registeredEmail.value = response.email
    toast.success('Cuenta creada. Por favor verifica tu email.')
    
  } catch (error) {
    console.error('Error en registro:', error)
    isLoading.value = false
    
    // Limpiar mensajes anteriores
    errorMessage.value = ''
    showAccountExistsMessage.value = false
    
    // Manejar casos especiales del backend
    if (error.response?.data?.error === 'ACCOUNT_EXISTS_UNVERIFIED') {
      // Usuario ya existe pero no verificado - mostrar mensaje verde con link
      showAccountExistsMessage.value = true
      accountExistsMessage.value = error.response.data.message
      return
    }
    
    if (error.response?.data?.error === 'ACCOUNT_NOT_VERIFIED') {
      // Usuario ya existe pero no verificado (caso de reenvío automático)
      showVerification.value = true
      registeredEmail.value = error.response.data.email
      toast.warning(error.response.data.message)
      return
    }
    
    if (error.response?.data?.error === 'ACCOUNT_EXISTS') {
      // Usuario ya existe y está activo
      toast.error(error.response.data.message)
      setTimeout(() => {
        router.push('/login')
      }, 2000)
      return
    }
    
    // Si es un error de validación con array de errores
    if (error.errors && Array.isArray(error.errors)) {
      // Mostrar cada error en un toast separado
      error.errors.forEach(errorMsg => {
        toast.error(errorMsg, {
          timeout: 6000,
          icon: '❌'
        });
      });
      
      // Actualizar mensaje de error general
      errorMessage.value = error.errors.join('\n');
      
      // Mapear campos del backend a campos del formulario
      const fieldMapping = {
        'email': 'email',
        'usuario': 'username',
        'contraseña': 'password',
        'nombre': 'first_name',
        'apellido': 'last_name'
      };
      
      // Marcar campos con error
      error.errors.forEach(errorMsg => {
        const [field] = errorMsg.toLowerCase().split(':');
        const formField = fieldMapping[field];
        
        if (formField && v$.value[formField]) {
          // Forzar la revalidación del campo
          v$.value[formField].$touch();
        }
      });
      return
    }
    
    // Error genérico
    toast.error(error.response?.data?.message || error.message || 'Error al crear la cuenta. Por favor, intenta nuevamente.');
    errorMessage.value = error.response?.data?.message || error.message;
  } finally {
    isLoading.value = false
  }
}

const handleCodeInput = (event) => {
  // Permitir solo números
  event.target.value = event.target.value.replace(/[^0-9]/g, '')
}

const verifyCode = async () => {
  try {
    isVerifying.value = true
    await verifyEmail({
      email: registeredEmail.value,
      code: verificationCode.value
    })
    
    toast.success('Email verificado correctamente')
    router.push('/login')
  } catch (error) {
    toast.error(error.response?.data?.error || 'Error al verificar el código')
  } finally {
    isVerifying.value = false
  }
}

const resendCode = async () => {
  try {
    isResending.value = true
    await resendVerification(registeredEmail.value)
    toast.success('Nuevo código enviado')
    verificationCode.value = ''
  } catch (error) {
    toast.error(error.response?.data?.error || 'Error al reenviar el código')
  } finally {
    isResending.value = false
  }
}
</script>

<style scoped>
.login-box {
  transition: all 0.3s ease;
}

.form-floating input {
  padding-right: 2.5rem;
}

.password-toggle {
  position: absolute;
  top: 50%;
  right: 1rem;
  transform: translateY(-50%);
  cursor: pointer;
  color: #6c757d;
  z-index: 10;
}

.password-toggle:hover {
  color: #495057;
}

.form-control:focus {
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.15);
}

.btn-primary {
  transition: all 0.2s ease;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(13, 110, 253, 0.2);
}

.btn-primary:active:not(:disabled) {
  transform: translateY(0);
}

@media (max-width: 576px) {
  .login-box {
    margin: 1rem;
  }
}

.verification-code-input input {
  font-size: 1.5rem;
  letter-spacing: 0.5rem;
  text-align: center;
}

.btn-link {
  color: var(--primary-color);
  text-decoration: none;
}

.btn-link:hover {
  text-decoration: underline;
}

/* Tooltip de Instagram */
.instagram-tooltip {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  cursor: help;
}

.instagram-tooltip .fas.fa-info-circle {
  font-size: 1rem;
  color: #6c757d;
  transition: color 0.2s ease;
}

.instagram-tooltip:hover .fas.fa-info-circle {
  color: #495057;
}

.tooltip-content {
  position: absolute;
  right: 25px;
  top: 50%;
  transform: translateY(-50%);
  background: #343a40;
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.85rem;
  line-height: 1.4;
  width: 280px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  z-index: 1000;
}

.tooltip-content::after {
  content: '';
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  border: 8px solid transparent;
  border-left-color: #343a40;
}

.instagram-tooltip:hover .tooltip-content {
  opacity: 1;
  visibility: visible;
}

/* Ajustar padding del input de Instagram para hacer espacio al icono */
#instagram {
  padding-right: 3rem !important;
}

@media (max-width: 768px) {
  .instagram-tooltip {
    right: -10px;
  }
  
  .tooltip-content {
    right: 25px;
    width: 250px;
    font-size: 0.8rem;
  }
}

@media (max-width: 576px) {
  .tooltip-content {
    right: 25px;
    width: 200px;
    font-size: 0.75rem;
    padding: 10px 12px;
  }
}
</style>
